import axios, { AxiosResponse } from "axios";
import { isString } from "lodash-es";

import {
  FileDto,
  FileReferenceDto,
  GeneralAttachmentDto,
  GeneralAttachmentInputDto,
  MimeBaseType,
} from "@/core/api/generated/v1";
import { IDownloadFileApiResult } from "@/core/api/types/downloadFileApiResult";

import { EncodingHelper } from "./encoding";
import { HttpHelper } from "./http";
import { UrlHelper } from "./url";

const baseMimeTypeMap: Record<string, MimeBaseType> = {
  application: MimeBaseType.Application,
  audio: MimeBaseType.Audio,
  image: MimeBaseType.Image,
  message: MimeBaseType.Message,
  model: MimeBaseType.Model,
  multipart: MimeBaseType.Multipart,
  text: MimeBaseType.Text,
  video: MimeBaseType.Video,
};

// NB:
// Blob: represents raw, immutable binary data (e.g., text, images, binary files).
// File: extends Blob and adds file-specific metadata like name, last modified date.
// Used File when working with user-uploaded files (e.g., from <input type="file">).
//
// Blob URL: a reference to blob data stored in browser memory
// Example: blob:http://localhost:3000/e6c41498-f5cc-4d81-a803-7be5e278582b
//
// Data URL: contains actual data encoded in the URL
// Example: data:[mimeType];base64,[base64EncodedData]

export class FileHelper {
  public static blobToBlobUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  public static blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(
          isString(reader.result)
            ? reader.result
            : reader.result instanceof ArrayBuffer
              ? reader.result.toString()
              : "",
        );
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  public static blobToFile(blob: Blob, fileName: string, mimeType?: string): File {
    return new File([blob], fileName, {
      type: blob.type || mimeType,
    });
  }

  public static fileToBlobUrl(file: File): string {
    return this.blobToBlobUrl(file);
  }

  public static fileToDataUrl(file: File): Promise<string> {
    return this.blobToDataUrl(file);
  }

  public static async fileUrlToBlob(fileUrl: string): Promise<Blob> {
    const response = await fetch(fileUrl, {
      // mode: "cors", // or 'no-cors' for opaque response
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch file by URL ${fileUrl}. Response status: ${response.status}.`,
      );
    }

    const blob = await response.blob();
    return blob;
  }

  public static async fileUrlToFile(fileUrl: string, fileName: string): Promise<File> {
    const blob = await this.fileUrlToBlob(fileUrl);
    return this.blobToFile(blob, fileName);
  }

  public static async fileUrlToDataUrl(fileUrl: string): Promise<string> {
    const blob = await this.fileUrlToBlob(fileUrl);
    return this.blobToDataUrl(blob);
  }

  public static dataUrlToBlob(dataUrl: string, mimeType?: string): Blob {
    // Extract MIME type
    const mimeMatch = dataUrl.match(/data:([^;]+)/);
    const mime = mimeType || (mimeMatch ? mimeMatch[1] : "application/octet-stream");

    // Extract base64 data
    const base64Data = dataUrl.split(",")[1];

    // Decode base64 to ArrayBuffer (base64 contains only ASCII characters)
    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mime });
    return blob;
  }

  public static dataUrlToFile(dataUrl: string, filename: string, mimeType?: string): File {
    const blob = this.dataUrlToBlob(dataUrl, mimeType);
    return this.blobToFile(blob, filename, mimeType);
  }

  public static stringToBlob(str: string, mimeType: string): Blob {
    const utf8Bytes = EncodingHelper.stringToUtf8Bytes(str);
    const blob = new Blob([utf8Bytes], { type: mimeType });
    return blob;
  }

  public static binaryStringToBlob(str: string, mimeType: string): Blob {
    const utf8Bytes = EncodingHelper.binaryStringToUtf8Bytes(str);
    const blob = new Blob([utf8Bytes], { type: mimeType });
    return blob;
  }

  /** Get Blob from API response. */
  public static async getDownloadFileApiResult(
    downloadFileFunc: () => Promise<AxiosResponse<any, any>>,
  ): Promise<IDownloadFileApiResult> {
    const response = await downloadFileFunc();
    const parsedContent = HttpHelper.parseContentHeaders(response.headers);

    const downloadResult: IDownloadFileApiResult = {
      blob: response.data as Blob,
      contentType: parsedContent.contentType!,
      filename: parsedContent.contentDispositionInfo?.directives?.filename,
    };
    return downloadResult;
  }

  /** Initiates browser download of specified data URL. */
  public static downloadDataUrl(dataUrl: string, filename: string) {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );

    document.body.removeChild(link);
  }

  /** Initiates browser download of specified Blob. */
  public static downloadBlob(blob: Blob, filename: string) {
    const blobUrl = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement("a");

    // Set link's href to point to the Blob URL
    link.href = blobUrl;
    link.download = filename;

    // Append link to the body
    document.body.appendChild(link);

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );

    // Remove link from body
    document.body.removeChild(link);
  }

  /** Initiates browser download of specified File. */
  public static downloadFile(file: File) {
    this.downloadBlob(file, file.name || "file.unknown");
  }

  /** Initiates browser download of file from specified file URL. */
  public static downloadFileByUrl(fileUrl: string, filename: string) {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = filename;

    document.body.appendChild(link);

    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );

    document.body.removeChild(link);
  }

  /** Initiates browser download of specified Blob. */
  public static downloadBlobFromApiResult(result: IDownloadFileApiResult) {
    this.downloadBlob(result.blob, result.filename || "file.unknown");
  }

  public static openFileByUrl(fileUrl: string, filename: string, openInNewTab = true) {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = filename;
    link.target = openInNewTab ? "_blank" : "";

    document.body.appendChild(link);

    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );

    document.body.removeChild(link);
  }

  /** E.g. image/jpeg -> image */
  public static getBaseMimeType(mimeType?: string): MimeBaseType {
    if (!mimeType) {
      return MimeBaseType.None;
    }
    const baseType = mimeType.split("/")[0].toLowerCase();
    return baseMimeTypeMap[baseType] || MimeBaseType.Unknown;
  }

  public static getFileType(mimeType?: string) {
    const baseMimeType = FileHelper.getBaseMimeType(mimeType);

    const result = {
      mimeType,
      isApplication: baseMimeType === MimeBaseType.Application,
      isAudio: baseMimeType === MimeBaseType.Audio,
      isImage: baseMimeType === MimeBaseType.Image,
      isVideo: baseMimeType === MimeBaseType.Video,
      isOther: false,
    };
    result.isOther = [result.isApplication, result.isAudio, result.isImage, result.isVideo].every(
      (x) => x === false,
    );

    return result;
  }

  /** Returns file extension (with leading dot by default). */
  public static getFileExtension(
    filename?: string | null,
    withLeadingDot = true,
  ): string | undefined {
    if (!filename) {
      return undefined;
    }
    if (UrlHelper.checkValidHttpUrl(filename)) {
      filename = new URL(filename).pathname;
    }
    return filename && /[.]/.test(filename)
      ? (withLeadingDot ? /\.[^.]+$/ : /[^.]+$/).exec(filename)![0]
      : undefined;
  }

  /** Returns matching thumbnail URL or file URL. */
  public static getFileThumbnailUrlOrDefault(
    file?: FileDto | FileReferenceDto | null,
    desiredSize?: IImageSize,
    sizeDelta: IImageSize = {
      width: 50,
      height: 50,
    },
  ): string | null | undefined {
    if (!file) {
      return null;
    }

    // return original URL if no desired size
    if (!desiredSize) {
      return file.url;
    }

    const thumbnail = file.thumbnails?.find(
      (x) =>
        x.size!.width! >= Math.max(desiredSize.width - sizeDelta.width, 0) &&
        x.size!.width! <= desiredSize.width + sizeDelta.width &&
        x.size!.height! >= Math.max(desiredSize.height - sizeDelta.height, 0) &&
        x.size!.height! <= desiredSize.height + sizeDelta.height,
    );
    return thumbnail?.url || file.url;
  }

  /** Sends HTTP HEAD request to see if file is available at the URL. */
  public static async checkFileExistsByUrl(fileUrl: string | null | undefined): Promise<boolean> {
    if (!fileUrl) {
      return false;
    }
    const response = await axios.head(fileUrl);
    return response.status >= 200 && response.status <= 299;
  }

  // #region Attachments

  public static toGeneralAttachmentInputDtoList(
    attachments: Array<GeneralAttachmentDto | GeneralAttachmentInputDto> | null | undefined,
    options?: { mustBeCopied?: boolean },
  ): GeneralAttachmentInputDto[] | undefined {
    return attachments?.map((x) => ({ ...x, mustBeCopied: options?.mustBeCopied ?? false }));
  }

  // #endregion
}
